/**
 * Live-scrubber validation card presentation.
 *
 * Prefer backend-provided `err.presentation`. The backend owns rule-specific
 * interpretation; the frontend only maps card-ready templates and segments into
 * the existing validation card layout.
 */

import type { OasisValidationError } from '../../services/api/types.gen';

/** Supporting line rendered under the question headline on a validation card. */
export type ValidationCardSupporting = {
  emptyLabel: string | null;
  primaryText: string | null;
  current: string | null;
  /** Prefix before `current`; defaults to "Current" in the card renderer. */
  currentPrefix?: string;
  /** Whether to render `current` as monospace code or plain text. Defaults to 'code'. */
  currentVariant?: 'text' | 'code';
  allowedPreview: string;
  /** Whether to render `allowedPreview` as monospace code or plain text. Defaults to 'code'. */
  allowedVariant?: 'text' | 'code';
  allowedExtra: number;
  hasSupporting: boolean;
};

const GENERIC_CHECKBOX_VALUE =
  /^(checked \(yes\)|not checked \(no\)|yes|no|0|1)$/i;

/**
 * Checkbox consistency rules often surface resolved values like "Checked (Yes)"
 * which tell the user nothing. Prefer the failed field's human label instead.
 */
export const formatConflictingFieldCurrent = (
  current: string | null,
  oasisFields: string[],
  fieldLabels: Record<string, string> | undefined
): { current: string | null; currentPrefix?: string } => {
  const isGeneric =
    !!current && GENERIC_CHECKBOX_VALUE.test(current.trim().toLowerCase());

  if (!isGeneric) {
    return { current };
  }

  if (!oasisFields.length) {
    return { current: null };
  }

  const names = oasisFields
    .map(f => fieldLabels?.[f]?.trim())
    .filter((name): name is string => !!name);

  if (!names.length) {
    return { current: null };
  }

  if (names.length === 1) {
    return { current: names[0], currentPrefix: 'Also checked' };
  }
  if (names.length <= 3) {
    return { current: names.join(', '), currentPrefix: 'Also checked' };
  }
  return {
    current: `${names[0]} and ${names.length - 1} more`,
    currentPrefix: 'Also checked',
  };
};

const emptySupporting = (): ValidationCardSupporting => ({
  emptyLabel: null,
  primaryText: null,
  current: null,
  allowedPreview: '',
  allowedExtra: 0,
  hasSupporting: false,
});

const findSegment = (
  segments: NonNullable<OasisValidationError['presentation']>['segments'],
  label: string
) => segments?.find(segment => segment.label === label) ?? null;

/**
 * Build the supporting line from structured backend presentation.
 * Returns null when the error has no presentation (caller should use fallback).
 */
export const buildValidationCardFromPresentation = (
  err: OasisValidationError,
  hasQuestionHeadline: boolean
): ValidationCardSupporting | null => {
  const p = err.presentation;
  if (!p) return null;

  if (p.template === 'singleLine') {
    // Map to emptyLabel (not primaryText) so the renderer styles it as a
    // standalone status phrase (font-medium, muted) rather than running it
    // through highlightValidationText or pairing it with a Current/Allowed line.
    return {
      emptyLabel: p.primaryText ?? null,
      primaryText: null,
      current: null,
      allowedPreview: '',
      allowedExtra: 0,
      hasSupporting: !!p.primaryText,
    };
  }

  if (p.template === 'inlineSegments') {
    const primaryText = hasQuestionHeadline
      ? (p.segments?.find(segment => !segment.label)?.text ?? null)
      : null;
    const allowedSeg = findSegment(p.segments, 'Allowed');
    const currentSeg =
      p.segments?.find(
        segment => !!segment.label && segment.label !== 'Allowed'
      ) ?? null;

    return {
      emptyLabel: null,
      primaryText,
      current: currentSeg?.text ?? null,
      currentPrefix: currentSeg?.label ?? undefined,
      currentVariant: currentSeg?.variant ?? 'code',
      allowedPreview: allowedSeg?.text ?? '',
      allowedVariant: allowedSeg?.variant ?? 'code',
      allowedExtra: 0,
      hasSupporting: !!(primaryText || currentSeg?.text || allowedSeg?.text),
    };
  }

  // Hidden fallbacks are metadata/config holes; keep the clinician-facing card quiet.
  if (p.template === 'hiddenFallback') return emptySupporting();

  return emptySupporting();
};
